/**
 * PATH: packages/ui/src/kanban/components/KanbanCardTags.tsx
 * FILE_ID: UI-KANBAN-CARD-TAGS-001
 * PURPOSE: The Trello-style tag pill row pinned to the top of an expanded card.
 * WHY: Extracted from KanbanCard.tsx (≤500-line working limit). Pure: card in,
 *   pill row out; renders nothing when the card has no tags so the DOM matches
 *   the former inline `card.tags.length > 0` guard.
 * RELATED:
 *   - ./KanbanCard.tsx (renders this only in expanded density).
 *   - ../utils readableForeground (luminance-derived chip foreground).
 */
import { Tag as TagIcon } from 'lucide-react'

import type { Card as KanbanCardModel } from '../types'
import { readableForeground } from '../utils'

export interface KanbanCardTagsProps {
  card: KanbanCardModel
}

export function KanbanCardTags({ card }: KanbanCardTagsProps) {
  if (card.tags.length === 0) return null
  return (
    <div className="mb-1.5 flex flex-wrap gap-1">
      {card.tags.slice(0, 4).map((t) => {
        const color = t.tag.color || '#94a3b8'
        const fg = readableForeground(color)
        // ARC-4 U09 two-colour labels — the optional accent renders as an
        // inset border so the pill keeps its solid fill footprint.
        const accent = t.tag.accent_color
        return (
          <span
            key={t.id}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none"
            style={{
              background: color,
              color: fg,
              boxShadow: accent ? `inset 0 0 0 1.5px ${accent}` : undefined,
            }}
            // FEAT-2026-07-04-KANBAN-LABEL-HOVER-NAME — the tooltip spells the
            // label NAME (the slug/path read as noise on hover).
            title={t.tag.name}
          >
            <TagIcon className="w-2 h-2" />
            {t.tag.name}
          </span>
        )
      })}
      {card.tags.length > 4 && (
        <span className="self-center text-[10px] font-medium text-neutral-500 dark:text-zinc-400">
          +{card.tags.length - 4}
        </span>
      )}
    </div>
  )
}
